/**
 * UTM Grid Utilities for Flight Planner
 *
 * Provides UTM ↔ WGS84 coordinate conversion, grid square generation within
 * a map viewport, and polygon merging of selected grid squares.
 *
 * Grid squares are aligned to UTM coordinates for consistency with standard
 * topographic maps used in Norwegian forestry.
 *
 * @module utils/utmGrid
 */

import { toPoint } from 'mgrs';

// ─── WGS84 Ellipsoid Constants ───────────────────────────────────────────────

const WGS84 = {
	a: 6378137.0, // Semi-major axis (meters)
	f: 1 / 298.257223563, // Flattening
};

const UTM_SCALE_FACTOR = 0.9996;

// Derived eccentricity values (computed once)
const e2 = 2 * WGS84.f - WGS84.f * WGS84.f;
const e4 = e2 * e2;
const e6 = e4 * e2;
const ep2 = e2 / (1 - e2);
const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));

// ─── UTM Zone Determination ──────────────────────────────────────────────────

/**
 * Get the UTM zone number for a given longitude.
 * Includes special handling for Norway (zone 32V extension).
 *
 * @param {number} lng - Longitude in degrees
 * @returns {number} UTM zone number (1-60)
 */
const getUTMZone = lng => {
	if (lng >= 3 && lng < 12) return 32; // Norway zone 32V extension
	return Math.floor((lng + 180) / 6) + 1;
};

/**
 * Get the central meridian for a UTM zone.
 * @param {number} zone - UTM zone number
 * @returns {number} Central meridian in degrees
 */
const getCentralMeridian = zone => (zone - 1) * 6 - 180 + 3;

// ─── Coordinate Conversion ───────────────────────────────────────────────────

/**
 * Convert WGS84 lat/lng to UTM easting/northing.
 *
 * Uses the transverse Mercator series expansion on the WGS84 ellipsoid.
 * Accurate to sub-meter precision for typical flight planning areas.
 *
 * @param {number} lat - Latitude in degrees
 * @param {number} lng - Longitude in degrees
 * @returns {{ easting: number, northing: number, zone: number }}
 */
const latLngToUTM = (lat, lng) => {
	const zone = getUTMZone(lng);
	const centralMeridian = getCentralMeridian(zone);

	const latRad = (lat * Math.PI) / 180;
	const lngRad = ((lng - centralMeridian) * Math.PI) / 180;

	const sinLat = Math.sin(latRad);
	const cosLat = Math.cos(latRad);
	const tanLat = Math.tan(latRad);

	const N = WGS84.a / Math.sqrt(1 - e2 * sinLat * sinLat);
	const T = tanLat * tanLat;
	const C = ep2 * cosLat * cosLat;
	const A = cosLat * lngRad;

	const M =
		WGS84.a *
		((1 - e2 / 4 - (3 * e4) / 64 - (5 * e6) / 256) * latRad -
			((3 * e2) / 8 + (3 * e4) / 32 + (45 * e6) / 1024) * Math.sin(2 * latRad) +
			((15 * e4) / 256 + (45 * e6) / 1024) * Math.sin(4 * latRad) -
			((35 * e6) / 3072) * Math.sin(6 * latRad));

	const k0 = UTM_SCALE_FACTOR;

	const easting =
		k0 *
			N *
			(A +
				((1 - T + C) * A * A * A) / 6 +
				((5 - 18 * T + T * T + 72 * C - 58 * ep2) * A * A * A * A * A) / 120) +
		500000;

	let northing =
		k0 *
		(M +
			N *
				tanLat *
				((A * A) / 2 +
					((5 - T + 9 * C + 4 * C * C) * A * A * A * A) / 24 +
					((61 - 58 * T + T * T + 600 * C - 330 * ep2) * A * A * A * A * A * A) / 720));

	if (lat < 0) northing += 10000000;

	return { easting, northing, zone };
};

/**
 * Convert UTM easting/northing back to WGS84 lat/lng.
 *
 * @param {number} easting - UTM easting in meters
 * @param {number} northing - UTM northing in meters
 * @param {number} zone - UTM zone number
 * @param {boolean} [isNorthern=true] - Whether the point is in the northern hemisphere
 * @returns {{ lat: number, lng: number }}
 */
const utmToLatLng = (easting, northing, zone, isNorthern = true) => {
	const k0 = UTM_SCALE_FACTOR;

	const x = easting - 500000;
	const y = isNorthern ? northing : northing - 10000000;

	const M = y / k0;
	const mu = M / (WGS84.a * (1 - e2 / 4 - (3 * e4) / 64 - (5 * e6) / 256));

	const phi1 =
		mu +
		((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
		((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
		((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu);

	const sinPhi1 = Math.sin(phi1);
	const cosPhi1 = Math.cos(phi1);
	const tanPhi1 = Math.tan(phi1);

	const N1 = WGS84.a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1);
	const T1 = tanPhi1 * tanPhi1;
	const C1 = ep2 * cosPhi1 * cosPhi1;
	const R1 = (WGS84.a * (1 - e2)) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5);
	const D = x / (N1 * k0);

	const lat =
		phi1 -
		((N1 * tanPhi1) / R1) *
			((D * D) / 2 -
				((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D * D * D * D) / 24 +
				((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) *
					D *
					D *
					D *
					D *
					D *
					D) /
					720);

	const lng =
		(D -
			((1 + 2 * T1 + C1) * D * D * D) / 6 +
			((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D * D * D * D * D) /
				120) /
		cosPhi1;

	const centralMeridian = getCentralMeridian(zone);

	return {
		lat: (lat * 180) / Math.PI,
		lng: (lng * 180) / Math.PI + centralMeridian,
	};
};

// ─── Grid Square Generation ──────────────────────────────────────────────────

/** Maximum number of grid squares to generate per viewport (performance guard) */
const MAX_GRID_SQUARES = 2000;

/**
 * Generate UTM-aligned grid squares within a map viewport extent.
 *
 * Squares are snapped to UTM grid boundaries so they align with standard
 * topographic maps. Each square includes its UTM coordinates, WGS84 corners,
 * and a human-readable grid reference label.
 *
 * @param {{ minLat: number, maxLat: number, minLng: number, maxLng: number }} extent
 * @param {number} gridSizeMeters - Size of each grid square in meters (e.g. 500, 1000)
 * @returns {Array<GridSquare>} Array of grid square objects
 *
 * @typedef {Object} GridSquare
 * @property {string} id - Unique identifier: "zone-easting-northing-gridSize"
 * @property {string} label - Human-readable grid reference (e.g. "32/500E/6600N")
 * @property {{ lat: number, lng: number }[]} corners - [SW, SE, NE, NW] in WGS84
 * @property {{ lat: number, lng: number }} center - Center point in WGS84
 * @property {number} easting - SW corner UTM easting
 * @property {number} northing - SW corner UTM northing
 * @property {number} zone - UTM zone number
 */
export const generateGridSquares = (extent, gridSizeMeters) => {
	const { minLat, maxLat, minLng, maxLng } = extent;

	const centerLng = (minLng + maxLng) / 2;
	const centerLat = (minLat + maxLat) / 2;
	const zone = getUTMZone(centerLng);
	const isNorthern = centerLat >= 0;

	// Convert extent corners to UTM and snap to grid boundaries
	const sw = latLngToUTM(minLat, minLng);
	const ne = latLngToUTM(maxLat, maxLng);

	const startE = Math.floor(sw.easting / gridSizeMeters) * gridSizeMeters;
	const startN = Math.floor(sw.northing / gridSizeMeters) * gridSizeMeters;
	const endE = Math.ceil(ne.easting / gridSizeMeters) * gridSizeMeters;
	const endN = Math.ceil(ne.northing / gridSizeMeters) * gridSizeMeters;

	// Performance guard
	const numCols = Math.ceil((endE - startE) / gridSizeMeters);
	const numRows = Math.ceil((endN - startN) / gridSizeMeters);
	if (numCols * numRows > MAX_GRID_SQUARES) return [];

	const squares = [];

	for (let e = startE; e < endE; e += gridSizeMeters) {
		for (let n = startN; n < endN; n += gridSizeMeters) {
			const sw_ll = utmToLatLng(e, n, zone, isNorthern);
			const se_ll = utmToLatLng(e + gridSizeMeters, n, zone, isNorthern);
			const ne_ll = utmToLatLng(e + gridSizeMeters, n + gridSizeMeters, zone, isNorthern);
			const nw_ll = utmToLatLng(e, n + gridSizeMeters, zone, isNorthern);

			const center = {
				lat: (sw_ll.lat + ne_ll.lat) / 2,
				lng: (sw_ll.lng + ne_ll.lng) / 2,
			};

			// Skip squares outside the visible extent
			if (
				center.lat < minLat ||
				center.lat > maxLat ||
				center.lng < minLng ||
				center.lng > maxLng
			) {
				continue;
			}

			const eKm = Math.floor(e / 1000);
			const nKm = Math.floor(n / 1000);

			squares.push({
				id: `${zone}-${e}-${n}-${gridSizeMeters}`,
				label: `${zone}/${eKm}E/${nKm}N`,
				corners: [sw_ll, se_ll, ne_ll, nw_ll],
				center,
				easting: e,
				northing: n,
				zone,
			});
		}
	}

	return squares;
};

// ─── Grid Square Merging ─────────────────────────────────────────────────────

/**
 * Extract the grid size (in meters) from a square's ID string.
 * ID format: "zone-easting-northing-gridSize"
 *
 * @param {GridSquare} square
 * @returns {number} Grid size in meters
 */
const getGridSizeFromSquare = square => {
	const parts = square.id.split('-');
	return parseInt(parts[parts.length - 1], 10) || 500;
};

/**
 * Compute the convex hull of all grid square corners using Graham scan.
 * Used as a fallback when squares are non-contiguous (disconnected).
 *
 * @param {GridSquare[]} squares
 * @returns {{ lat: number, lng: number }[]} Convex hull vertices
 */
const computeConvexHull = squares => {
	const allPoints = squares.flatMap(sq => sq.corners);

	const cross = (O, A, B) =>
		(A.lng - O.lng) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lng - O.lng);

	allPoints.sort((a, b) => a.lng - b.lng || a.lat - b.lat);

	// Deduplicate within floating-point tolerance
	const unique = [allPoints[0]];
	for (let i = 1; i < allPoints.length; i++) {
		const prev = unique[unique.length - 1];
		if (
			Math.abs(allPoints[i].lat - prev.lat) > 1e-8 ||
			Math.abs(allPoints[i].lng - prev.lng) > 1e-8
		) {
			unique.push(allPoints[i]);
		}
	}
	if (unique.length < 3) return unique;

	// Lower hull
	const lower = [];
	for (const p of unique) {
		while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
			lower.pop();
		lower.push(p);
	}

	// Upper hull
	const upper = [];
	for (let i = unique.length - 1; i >= 0; i--) {
		const p = unique[i];
		while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
			upper.pop();
		upper.push(p);
	}

	lower.pop();
	upper.pop();
	return [...lower, ...upper];
};

/**
 * Collect boundary edges from a set of grid squares.
 *
 * Each square emits 4 clockwise edges. When two adjacent squares share an edge,
 * the forward and reverse versions cancel out. The remaining edges form the
 * outer boundary of the selection.
 *
 * @param {GridSquare[]} squares
 * @param {number} gs - Grid size in meters
 * @returns {Map<string, number>} Map of boundary edge keys → count
 */
const collectBoundaryEdges = (squares, gs) => {
	const edgeCount = new Map();
	const key = (e1, n1, e2, n2) => `${e1},${n1}>${e2},${n2}`;

	for (const sq of squares) {
		const e = sq.easting;
		const n = sq.northing;

		// Clockwise: bottom → right → top → left
		const edges = [
			[e, n, e + gs, n],
			[e + gs, n, e + gs, n + gs],
			[e + gs, n + gs, e, n + gs],
			[e, n + gs, e, n],
		];

		for (const [e1, n1, e2, n2] of edges) {
			const fwd = key(e1, n1, e2, n2);
			const rev = key(e2, n2, e1, n1);

			if (edgeCount.has(rev)) {
				// Shared internal edge — cancel both directions
				const c = edgeCount.get(rev);
				if (c === 1) edgeCount.delete(rev);
				else edgeCount.set(rev, c - 1);
			} else {
				edgeCount.set(fwd, (edgeCount.get(fwd) || 0) + 1);
			}
		}
	}

	return edgeCount;
};

/**
 * Build an adjacency map from boundary edges.
 * Maps each vertex to its outgoing boundary edges.
 *
 * @param {Map<string, number>} edgeCount - Boundary edges from collectBoundaryEdges
 * @returns {Map<string, Array<{ e: number, n: number, key: string }>>}
 */
const buildAdjacencyMap = edgeCount => {
	const adjacency = new Map();

	for (const [key] of edgeCount) {
		const [from, to] = key.split('>');
		const [e2, n2] = to.split(',').map(Number);

		if (!adjacency.has(from)) adjacency.set(from, []);
		adjacency.get(from).push({ e: e2, n: n2, key });
	}

	return adjacency;
};

/**
 * Determine the cardinal direction of an edge.
 * Returns 0=right, 1=up, 2=left, 3=down.
 *
 * @param {number} e1 - From easting
 * @param {number} n1 - From northing
 * @param {number} e2 - To easting
 * @param {number} n2 - To northing
 * @returns {number} Direction index (0-3)
 */
const getDirection = (e1, n1, e2, n2) => {
	const dx = e2 - e1;
	const dy = n2 - n1;
	if (dx > 0) return 0; // right
	if (dy > 0) return 1; // up
	if (dx < 0) return 2; // left
	if (dy < 0) return 3; // down
	return -1;
};

/**
 * Trace the outer boundary of connected grid squares by following edges clockwise.
 *
 * Starting from the bottom-left vertex, always prefers the rightmost turn
 * to stay on the outer boundary. This correctly handles concave shapes.
 *
 * @param {Map<string, Array>} adjacency - Vertex → outgoing edges map
 * @param {number} totalEdgeCount - Total number of boundary edges
 * @returns {{ ring: Array<{ e: number, n: number }>, allTraced: boolean }}
 */
const traceBoundary = (adjacency, totalEdgeCount) => {
	// Find the bottom-left-most vertex as starting point
	let startVertex = null;
	let minN = Infinity;
	let minE = Infinity;

	for (const [vertex] of adjacency) {
		const [e, n] = vertex.split(',').map(Number);
		if (n < minN || (n === minN && e < minE)) {
			minN = n;
			minE = e;
			startVertex = vertex;
		}
	}

	const ring = [];
	const usedEdges = new Set();
	let currentVertex = startVertex;
	let prevDir = 3; // Pretend we arrived from above → first preference is "right"
	let iterations = 0;

	while (iterations < totalEdgeCount + 1) {
		iterations++;
		const [ce, cn] = currentVertex.split(',').map(Number);
		ring.push({ e: ce, n: cn });

		const outgoing = adjacency.get(currentVertex);
		if (!outgoing || outgoing.length === 0) break;

		// Select the next edge using clockwise turn priority:
		// right turn (4) > straight (3) > left turn (2) > u-turn (1)
		let bestEdge = null;
		let bestPriority = -1;

		for (const edge of outgoing) {
			if (usedEdges.has(edge.key)) continue;

			const dir = getDirection(ce, cn, edge.e, edge.n);
			const turn = (dir - prevDir + 4) % 4;
			const priority = turn === 1 ? 4 : turn === 0 ? 3 : turn === 3 ? 2 : 1;

			if (priority > bestPriority) {
				bestPriority = priority;
				bestEdge = edge;
			}
		}

		if (!bestEdge) break;

		usedEdges.add(bestEdge.key);
		prevDir = getDirection(ce, cn, bestEdge.e, bestEdge.n);
		currentVertex = `${bestEdge.e},${bestEdge.n}`;

		if (currentVertex === startVertex) break;
	}

	return {
		ring,
		allTraced: usedEdges.size >= totalEdgeCount,
	};
};

/**
 * Remove collinear vertices from a ring of UTM points.
 * Keeps only vertices where the direction changes (corners).
 *
 * @param {Array<{ e: number, n: number }>} ring
 * @returns {Array<{ e: number, n: number }>} Simplified ring
 */
const removeCollinearVertices = ring => {
	if (ring.length < 3) return ring;

	const simplified = [];

	for (let i = 0; i < ring.length; i++) {
		const prev = ring[(i - 1 + ring.length) % ring.length];
		const curr = ring[i];
		const next = ring[(i + 1) % ring.length];

		const sameEasting = prev.e === curr.e && curr.e === next.e;
		const sameNorthing = prev.n === curr.n && curr.n === next.n;

		// Keep this vertex only if direction changes (it's a corner)
		if (!sameEasting && !sameNorthing) {
			simplified.push(curr);
		}
	}

	return simplified.length >= 3 ? simplified : ring;
};

/**
 * Merge selected grid squares into a single polygon.
 *
 * For contiguous selections (including concave shapes like L, U, T), traces the
 * outer boundary following grid lines exactly. For disconnected selections,
 * falls back to a convex hull.
 *
 * @param {GridSquare[]} squares - Selected grid square objects
 * @returns {{ lat: number, lng: number }[]} Polygon vertices in WGS84
 *
 * @example
 * // L-shaped selection → 6-vertex concave polygon
 * const polygon = mergeGridSquares([topLeft, bottomLeft, bottomRight]);
 *
 * @example
 * // 2x2 block → 4-vertex rectangle
 * const polygon = mergeGridSquares([sw, se, nw, ne]);
 */
export const mergeGridSquares = squares => {
	if (squares.length === 0) return [];
	if (squares.length === 1) return [...squares[0].corners];

	const zone = squares[0].zone;
	const isNorthern = squares[0].corners[0].lat >= 0;
	const gs = getGridSizeFromSquare(squares[0]);

	// Step 1: Find boundary edges (internal shared edges cancel out)
	const edgeCount = collectBoundaryEdges(squares, gs);

	// Step 2: Build vertex → outgoing edge adjacency
	const adjacency = buildAdjacencyMap(edgeCount);

	// Step 3: Trace the outer boundary clockwise
	const { ring, allTraced } = traceBoundary(adjacency, edgeCount.size);

	// Step 4: Handle disconnected selections
	if (!allTraced || ring.length < 3) {
		return computeConvexHull(squares);
	}

	// Step 5: Simplify (remove collinear points along straight grid lines)
	const simplified = removeCollinearVertices(ring);

	// Step 6: Convert UTM vertices back to WGS84
	return simplified.map(pt => utmToLatLng(pt.e, pt.n, zone, isNorthern));
};

/** @deprecated Use mergeGridSquares directly — it now handles concave shapes. */
export const mergeGridSquaresExact = mergeGridSquares;

// ─── Grid Reference Parsing ──────────────────────────────────────────────────

/**
 * Parse a UTM/MGRS grid reference string and return WGS84 coordinates.
 *
 * Supported formats:
 *   - Internal label: "32/500E/6600N"
 *   - UTM coordinate: "32V 0661965 5728316" or "32 661965 5728316"
 *   - MGRS: "32V NM 61965 28316" or "32VNM6196528316"
 *
 * @param {string} input - Grid reference string
 * @returns {{ lat: number, lng: number } | null} Coordinates or null if invalid
 */
export const parseGridReference = input => {
	const trimmed = input.trim().toUpperCase();
	if (!trimmed) return null;

	// Internal label format — "32/500E/6600N"
	const labelMatch = trimmed.match(/^(\d{1,2})\/(\d+)E\/(\d+)N$/);
	if (labelMatch) {
		const zone = parseInt(labelMatch[1], 10);
		const eastingKm = parseInt(labelMatch[2], 10);
		const northingKm = parseInt(labelMatch[3], 10);
		return utmToLatLng(eastingKm * 1000, northingKm * 1000, zone, true);
	}

	// UTM coordinate format — "32V 0661965 5728316" or "32 661965 5728316"
	const utmMatch = trimmed.match(/^(\d{1,2})\s*([A-Z])?\s+(\d{5,7})\s+(\d{5,7})$/);
	if (utmMatch) {
		const zone = parseInt(utmMatch[1], 10);
		const band = utmMatch[2];
		const easting = parseInt(utmMatch[3], 10);
		const northing = parseInt(utmMatch[4], 10);
		const isNorthern = band ? band >= 'N' : true;
		return utmToLatLng(easting, northing, zone, isNorthern);
	}

	// MGRS format — delegate to mgrs library
	try {
		const normalized = trimmed.replace(/\s+/g, '');
		const [lng, lat] = toPoint(normalized);
		if (isNaN(lat) || isNaN(lng)) return null;
		return { lat, lng };
	} catch {
		return null;
	}
};
